/**
 * Validation rules for Onboarding Wizard steps
 */

const isValidEmail = (email) => {
  if (!email) return false;
  const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return re.test(email);
};

const isValidDomain = (domain) => {
  if (!domain) return false;
  const domainLower = domain.toLowerCase();
  if (domainLower.includes("www.")) return false;
  const re =
    /^(?=.{1,253}\.?$)(?:(?!-|[^.]+_)[A-Za-z0-9-_]{1,63}(?<!-)(?:\.|$)){2,}$/;
  return re.test(domain);
};

const isValidPassword = (password) => {
  if (!password) return false;
  return password.length >= 8 && /[A-Z]/.test(password);
};

export const stepValidationRules = {
  // Step 1: Group Details
  1: [
    {
      // Validation for NEW group mode
      condition: (data) => data.mode === "new",
      validator: (data) => {
        const errors = {};
        let isValid = true;

        // Name validation
        if (!data.name?.trim()) {
          errors.name = "Group name is required";
          isValid = false;
        }

        // Email validation
        if (!data.email) {
          errors.email = "Email is required";
          isValid = false;
        } else if (!isValidEmail(data.email)) {
          errors.email = "Valid email is required";
          isValid = false;
        }

        // Password validation
        if (!data.password) {
          errors.password = "Password is required";
          isValid = false;
        } else if (!isValidPassword(data.password)) {
          errors.password =
            "Password must be at least 8 characters with at least one uppercase letter";
          isValid = false;
        }

        // Company name validation
        if (!data.company_name?.trim()) {
          errors.company_name = "Company name is required";
          isValid = false;
        }

        // Company domain validation
        if (!data.company_domain) {
          errors.company_domain = "Company domain is required";
          isValid = false;
        } else if (!isValidDomain(data.company_domain)) {
          errors.company_domain = "Valid domain is required (without www)";
          isValid = false;
        }

        // Seller type validation
        if (!data.seller_type) {
          errors.seller_type = "Group type is required";
          isValid = false;
        }

        // Contact person name validation
        if (!data.contact_person_name?.trim()) {
          errors.contact_person_name = "Contact person name is required";
          isValid = false;
        }

        // Contact person email validation
        if (!data.contact_person_email) {
          errors.contact_person_email = "Contact person email is required";
          isValid = false;
        } else if (!isValidEmail(data.contact_person_email)) {
          errors.contact_person_email = "Valid contact email is required";
          isValid = false;
        }

        return { isValid, errors };
      },
    },
    {
      // Validation for EXISTING group mode
      condition: (data) => data.mode === "existing",
      validator: (data) => {
        const errors = {};
        let isValid = true;

        if (!data.selectedGroupId) {
          errors.selectedGroup = "Please select a group";
          isValid = false;
        }

        return { isValid, errors };
      },
    },
  ],

  // Step 2: Seller Creation
  2: [
    {
      validator: (data) => {
        const errors = {};
        let isValid = true;

        // Check if at least one seller exists
        if (!data.sellers || data.sellers.length === 0) {
          errors.sellers = "At least one seller is required";
          isValid = false;
          return { isValid, errors };
        }

        // Validate each seller
        data.sellers.forEach((seller, index) => {
          // Name validation
          if (!seller.name?.trim()) {
            errors[`seller_${index}_name`] = `Seller ${
              index + 1
            }: Name is required`;
            isValid = false;
          }

          // Domain validation
          if (!seller.domain?.trim()) {
            errors[`seller_${index}_domain`] = `Seller ${
              index + 1
            }: Domain is required`;
            isValid = false;
          } else {
            const domain = seller.domain.toLowerCase();
            if (domain.includes("www.")) {
              errors[`seller_${index}_domain`] = `Seller ${
                index + 1
              }: Domain should not include www`;
              isValid = false;
            }
          }

          // Password validation
          if (!seller.password?.trim()) {
            errors[`seller_${index}_password`] = `Seller ${
              index + 1
            }: Password is required`;
            isValid = false;
          }

          // Seller type validation
          if (!seller.seller_type) {
            errors[`seller_${index}_type`] = `Seller ${
              index + 1
            }: Seller type is required`;
            isValid = false;
          }

          if (seller.content_media_type !== "snippet") {
            // Contract type validation
            if (!seller.contract_type) {
              errors[`seller_${index}_contract`] = `Seller ${
                index + 1
              }: Contract type is required`;
              isValid = false;
            }

            // Contract value validation
            if (!seller.contract_value || seller.contract_value <= 0) {
              errors[`seller_${index}_value`] = `Seller ${
                index + 1
              }: Contract value must be greater than 0`;
              isValid = false;
            }

            // Additional validation for CPM contract
            if (seller.contract_type === "cpm" && seller.contract_value > 100) {
              errors[`seller_${index}_value`] = `Seller ${
                index + 1
              }: CPM value seems too high`;
              isValid = false;
            }

            // Additional validation for RevShare contract
            if (
              seller.contract_type === "revshare" &&
              (seller.contract_value < 5 || seller.contract_value > 75)
            ) {
              errors[`seller_${index}_value`] = `Seller ${
                index + 1
              }: Revenue share must be between 5% and 75%`;
              isValid = false;
            }
          }

          if (
            seller.content_media_type === "snippet" &&
            seller.content_type === "shows" &&
            (seller.selected_shows_snippet_count || 0) < 3
          ) {
            errors[`seller_${index}_shows`] = `Seller ${
              index + 1
            }: Selected show(s) must have at least 3 generated snippets combined`;
            isValid = false;
          }
        });

        return { isValid, errors };
      },
    },
  ],

  // Step 3: Summary & Export
  // No validation needed - just display and actions
  3: [
    {
      validator: () => {
        return { isValid: true, errors: {} };
      },
    },
  ],
};
